/**
 * Per-developer workload aggregation for the current sprint.
 *
 * Pure function (no I/O) so it can be unit-tested against snapshot fixtures the
 * same way the backend parsers are. Groups current-sprint items by assignee and
 * splits each person's load by priority and by pipeline stage.
 *
 * The four stage buckets are mutually exclusive and sum to `total`, so
 * `remaining === total - done === toDo + inDev + inQa`.
 */
import type { WorkItem } from "@/types/sprint-report"

export const UNASSIGNED = "(unassigned)"

export interface WorkloadRow {
  assignee: string
  isUnassigned: boolean
  p0: number
  p1: number
  p2: number
  p3: number
  toDo: number // pre-dev (New/Grooming/Ready/Dev Ready) + any unknown state
  inDev: number // Committed/Dev WIP/Dev PR/Dev Done
  inQa: number // QA Ready/QA WIP
  done: number // Done/Closed
  total: number
  remaining: number // total - done
  bundlesLed: number // current-sprint bundles this person leads (Standup owner)
}

function emptyRow(assignee: string): WorkloadRow {
  return {
    assignee,
    isUnassigned: assignee === UNASSIGNED,
    p0: 0,
    p1: 0,
    p2: 0,
    p3: 0,
    toDo: 0,
    inDev: 0,
    inQa: 0,
    done: 0,
    total: 0,
    remaining: 0,
    bundlesLed: 0,
  }
}

/**
 * Build per-developer rows for the current sprint, sorted by remaining work
 * (busiest first) with the unassigned bucket pinned last.
 */
export function aggregateWorkload(
  items: WorkItem[],
  bundlesLed: Record<string, number> = {},
): WorkloadRow[] {
  const byAssignee = new Map<string, WorkloadRow>()
  const getRow = (name: string): WorkloadRow => {
    let row = byAssignee.get(name)
    if (!row) {
      row = emptyRow(name)
      byAssignee.set(name, row)
    }
    return row
  }

  for (const item of items) {
    // Workload is about the active sprint; ignore backlog/future/previous and
    // anything that was removed.
    if (!item.sprint?.is_current) continue
    if (item.state_category === "removed") continue

    const name = item.assigned_to?.name?.trim() || UNASSIGNED
    const row = getRow(name)

    switch (item.priority.major) {
      case "P0":
        row.p0 += 1
        break
      case "P1":
        row.p1 += 1
        break
      case "P2":
        row.p2 += 1
        break
      case "P3":
        row.p3 += 1
        break
    }

    switch (item.state_category) {
      case "done":
        row.done += 1
        break
      case "in_qa":
        row.inQa += 1
        break
      case "in_dev":
      case "dev_complete":
        row.inDev += 1
        break
      default:
        // pre_dev and any unrecognized state count as "to do / triage".
        row.toDo += 1
        break
    }

    row.total += 1
    row.remaining = row.total - row.done
  }

  // Attach bundle-lead counts — including leads who have no directly-assigned
  // current-sprint item of their own (they still get a row, all zeros).
  for (const [name, count] of Object.entries(bundlesLed)) {
    if (count > 0) getRow(name).bundlesLed = count
  }

  return [...byAssignee.values()].sort(compareRows)
}

/** Busiest first (most remaining), then most P0s, then name; unassigned last. */
function compareRows(a: WorkloadRow, b: WorkloadRow): number {
  if (a.isUnassigned !== b.isUnassigned) return a.isUnassigned ? 1 : -1
  if (b.remaining !== a.remaining) return b.remaining - a.remaining
  if (b.p0 !== a.p0) return b.p0 - a.p0
  return a.assignee.localeCompare(b.assignee)
}

/** Column-wise totals across all rows, for the footer row. */
export function sumWorkload(rows: WorkloadRow[]): WorkloadRow {
  const total = emptyRow("Total")
  total.isUnassigned = false
  for (const r of rows) {
    total.p0 += r.p0
    total.p1 += r.p1
    total.p2 += r.p2
    total.p3 += r.p3
    total.toDo += r.toDo
    total.inDev += r.inDev
    total.inQa += r.inQa
    total.done += r.done
    total.total += r.total
    total.remaining += r.remaining
    total.bundlesLed += r.bundlesLed
  }
  return total
}
